import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="pe-birthday"
// Bubbly birthday overlay of the home page (behind the PE_BIRTHDAY_OVERLAY feature flag).
// Hidden until its secret sequence: triple click on the Beneva logo, then on the Soprema one.
const SEQUENCE = ["beneva", "soprema"];
const MULTI_CLICK_MS = 500; // max gap between the clicks of a triple click
const SEQUENCE_MS = 15000; // max time between the two triple clicks

export default class extends Controller {
  static targets = ["canvas", "lenses", "foam", "board", "shadow", "dialog", "card", "modalCanvas"];

  connect() {
    this.sequenceStep = 0;
    this.clicks = { key: null, count: 0, at: 0 };
    // Capture phase: the logos sit in a swiper that may stop the click
    document.addEventListener("click", this.onSecretClick, true);
  }

  onSecretClick = (event) => {
    const key = event.target.closest?.("[data-pe-birthday-key]")?.dataset.peBirthdayKey;
    if (!key) return;

    const now = performance.now();
    const clicks = this.clicks;
    if (key !== clicks.key || now - clicks.at > MULTI_CLICK_MS) clicks.count = 0;
    clicks.key = key;
    clicks.at = now;
    if (++clicks.count < 3) return;
    clicks.count = 0;

    if (this.sequenceStep > 0 && now - this.sequenceAt > SEQUENCE_MS) this.sequenceStep = 0;
    if (key !== SEQUENCE[this.sequenceStep]) {
      this.sequenceStep = key === SEQUENCE[0] ? 1 : 0;
    } else {
      this.sequenceStep++;
    }
    this.sequenceAt = now;
    if (this.sequenceStep === SEQUENCE.length) this.boot();
  };

  // Reveals the overlay and fades it in
  async boot() {
    document.removeEventListener("click", this.onSecretClick, true);
    this.element.classList.add("is-fading");
    this.element.hidden = false;
    await this.start();
    requestAnimationFrame(() => {
      this.element.classList.add("is-shown");
      // No opacity left once shown: it would cut the lenses off the page they refract
      setTimeout(() => this.element.classList.remove("is-fading", "is-shown"), 1300);
    });
  }

  async start() {
    const scroller = document.getElementById("main-scroll");
    if (!scroller) return;

    // The bubble engine is only downloaded once the overlay is booted
    const [{ PageScene }, tricks] = await Promise.all([
      import("@/javascript/web/pe_birthday/page_scene"),
      import("@/javascript/web/pe_birthday/tricks")
    ]);
    if (!this.element.isConnected) return;

    this.pageScene = new PageScene({
      canvas: this.canvasTarget,
      foam: this.foamTarget,
      scroller,
      lenses: this.lensesTarget
    });
    this.pageScene.start();
    this.watchStage(scroller, tricks);
  }

  disconnect() {
    document.removeEventListener("click", this.onSecretClick, true);
    this.stageObserver?.disconnect();
    clearTimeout(this.trickTimer);
    this.pageScene?.stop();
    this.modalScene?.stop();
    if (this.dialogTarget.open) this.dialogTarget.close();
  }

  async open() {
    if (this.dialogTarget.open) return;

    const { ModalScene } = await import("@/javascript/web/pe_birthday/modal_scene");
    this.modalScene ||= new ModalScene({ canvas: this.modalCanvasTarget, card: this.cardTarget });

    this.closing = false;
    this.dialogTarget.showModal();
    this.modalScene.enter();
    requestAnimationFrame(() => this.dialogTarget.classList.add("is-open"));
  }

  close(event) {
    event?.preventDefault(); // Esc: let the bubbles pop before the dialog closes
    if (this.closing || !this.dialogTarget.open) return;

    this.closing = true;
    this.dialogTarget.classList.remove("is-open");
    this.modalScene.leave(() => {
      this.dialogTarget.close();
      this.closing = false;
    });
  }

  // The browser can still force the dialog closed (e.g. Esc pressed twice)
  closed() {
    this.modalScene?.stop();
    this.dialogTarget.classList.remove("is-open");
    this.closing = false;
  }

  closeFromBackdrop() {
    if (this.modalScene?.justPopped()) return;
    this.close();
  }

  // Logo and button pop in when the foam is reached, hide when scrolling back up
  watchStage(scroller, tricks) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.tricks = tricks;
    this.element.classList.add("is-staged");
    this.stageObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5 && !this.onStage) this.arrive();
        else if (entry.intersectionRatio < 0.2 && this.onStage) this.leave();
      },
      { root: scroller, threshold: [0, 0.2, 0.5] }
    );
    this.stageObserver.observe(this.foamTarget);
  }

  arrive() {
    this.onStage = true;
    this.element.classList.add("is-visible");
    this.scheduleTrick(550); // right after landing in
  }

  leave() {
    this.onStage = false;
    this.element.classList.remove("is-visible");
    clearTimeout(this.trickTimer);
  }

  scheduleTrick(delay = 3500 + Math.random() * 4500) {
    clearTimeout(this.trickTimer);
    this.trickTimer = setTimeout(() => {
      this.trick();
      this.scheduleTrick();
    }, delay);
  }

  trick() {
    if (!this.tricks || !this.onStage || this.boardTarget.getAnimations().length) return;

    const { board, shadow, duration } = this.tricks.trickKeyframes(this.tricks.randomTrick(), this.boardTarget.offsetWidth);
    this.boardTarget.animate(board, { duration });
    this.shadowTarget.animate(shadow, { duration });
  }

  popBubble(event) {
    this.modalScene?.popAt(event.clientX, event.clientY);
  }
}
